import React from 'react'
import { createPortal } from 'react-dom'
import { ApiError, api } from '../api/client'
import { saveErrorMessage } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import './Sheet.css'

interface RemoveCardSheetProps {
  clientId: string
  firstName: string
  onClose: () => void
  onRemoved: () => void
}

// RemoveCardSheet asks before taking a client's saved card off file.
export default function RemoveCardSheet({ clientId, firstName, onClose, onRemoved }: RemoveCardSheetProps) {
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  const remove = async () => {
    setBusy(true)
    setError('')
    try {
      await api.removeCard(clientId)
      onRemoved()
      return
    } catch (e) {
      // Already gone (another device): the aim is met.
      if (e instanceof ApiError && e.problem === 'no-saved-card') {
        onRemoved()
        return
      }
      setError(saveErrorMessage(e))
    }
    setBusy(false)
  }

  const sheet = (
    <div className="sheet-overlay" onClick={(e) => { e.stopPropagation(); if (!busy) onClose() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Remove card" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Remove {firstName}’s card?</h2>
        </div>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        {/* The final confirm is the only solid red, with the way back below it
            (docs/25 danger pattern). */}
        <div className="sheet-stack">
          <button type="button" className="btn btn-danger-solid btn-block" onClick={remove} disabled={busy}>
            Yes, remove it
          </button>
          <button type="button" className="btn btn-secondary btn-block" onClick={onClose} disabled={busy}>
            Keep it
          </button>
        </div>
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
